import { Chip, Tooltip } from '@mui/material';

import { getSectionsForApplication } from '@/config/loanProductRegistry';
import { isSectionComplete } from '@/utils/propertyView/stageResolver';
import type {
  LoanAdminInsights,
  LoanApplication,
  LoanAttestationState,
} from '@/types/LoanApplicationType';

const ATTESTATION: Record<
  LoanAttestationState,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  signed: { label: 'Signed', color: 'success' },
  needs_resign: { label: 'Needs re-signing', color: 'warning' },
  not_signed: { label: 'Not signed', color: 'default' },
};

export function AttestationChip({ state }: { state?: LoanAttestationState }) {
  const a = ATTESTATION[state ?? 'not_signed'];
  return (
    <Chip size="small" label={a.label} color={a.color} variant="outlined" />
  );
}

/** "2 of 5 provided" with the missing list on hover. */
export function DocumentsSummary({
  insights,
}: {
  insights?: LoanAdminInsights;
}) {
  if (!insights) return <>—</>;
  const { total, provided, missing } = insights.documents;
  if (!total) return <>No checklist</>;
  const text = missing.length ? `${missing.length} missing` : 'All provided';
  return (
    <Tooltip
      title={
        missing.length
          ? `Missing: ${missing.map((m) => m.label).join(', ')}`
          : `${provided} of ${total} provided`
      }
    >
      <span>
        {text} ({provided}/{total})
      </span>
    </Tooltip>
  );
}

export function daysAgoLabel(days: number | null | undefined) {
  if (days == null) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export function humanizeKey(v?: string | null) {
  if (!v) return '—';
  const s = v.replace(/[_-]+/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatMoney(n?: number | null) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

/** Name to show when a person never entered one (matches the server). */
export function displayNameOf(u?: {
  name?: string | null;
  email?: string | null;
}) {
  const name = String(u?.name || '').trim();
  if (name) return name;
  const email = String(u?.email || '');
  return email ? email.split('@')[0] : 'Unnamed';
}

/**
 * Sections complete the way the borrower's own progress bar counts them:
 * every section in the sidebar, with "Review & sign" done once the
 * attestation is current.
 */
export function sectionProgress(app: LoanApplication) {
  const sections = getSectionsForApplication(app, {
    bypassInvestorGate: true,
    standaloneShell: true,
    showPurchasePropertySection: true,
  });
  const done = sections.filter((s) => isSectionComplete(app, s.key));
  return {
    done: done.length,
    total: sections.length,
    incomplete: sections.filter((s) => !done.includes(s)).map((s) => s.label),
  };
}
